import { Component, computed, inject, input, signal, OnInit, OnDestroy } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { SearchFieldComponent } from '../../../../shared/components/search-field/search-field.component';
import { MatButtonModule } from '@angular/material/button';
import { toObservable } from '@angular/core/rxjs-interop';
import { WebsiteData } from '../../../../shared/models/website-overview.model';
import {
  BehaviorSubject,
  combineLatest,
  filter,
  map,
  shareReplay,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs';
import { DNSService } from '../../../../core/services/dns.service';
import { AsyncPipe } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTableModule } from '@angular/material/table';
import { TableComponent } from '../../../../shared/components/table/table.component';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AddDnsRecordDialogComponent } from '../../dialogs/add-dns-record-dialog/add-dns-record-dialog.component';
import { SnackbarComponent } from '../../../../shared/components/snackbar/snackbar.component';
import { DNSZoneRecord, DNSZoneStatus } from '../../../../shared/models/dns.models';
import { ConfirmationDialogComponent } from '../../../../shared/dialogs/confirmation-dialog/confirmation-dialog.component';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { StatusModalService } from '../../../../core/services/status-modal.service';
import { WebsocketService } from '../../../../core/services/websocket.service';
import { PageEvent } from '@angular/material/paginator';
import { Status } from '../../../../shared/models/status-label.model';
import { StatusLabelComponent } from '../../../../shared/components/status-label/status-label.component';

@Component({
  selector: 'app-dns-zone',
  imports: [
    MatCardModule,
    FontAwesomeModule,
    SearchFieldComponent,
    MatButtonModule,
    AsyncPipe,
    MatTooltipModule,
    MatTableModule,
    TableComponent,
    ClipboardModule,
    StatusLabelComponent,
  ],
  templateUrl: './dns-zone.component.html',
  styleUrl: './dns-zone.component.scss',
})
export class DNSZoneComponent implements OnInit, OnDestroy {
  private dnsService = inject(DNSService);

  private dialog = inject(MatDialog);

  private snackbar = inject(MatSnackBar);

  private statusModalService = inject(StatusModalService);

  private websocketService = inject(WebsocketService);

  websiteData = input.required<WebsiteData>();

  website = computed(() => this.websiteData().website);

  website$ = toObservable(this.website);

  refresh$ = new BehaviorSubject<void>(undefined);

  filters = signal<{ page: number; pageSize: number; domainSearch?: string }>({
    page: 1,
    pageSize: 10,
  });

  filters$ = toObservable(this.filters);

  zones$ = this.refresh$.pipe(
    switchMap(() => this.website$),
    switchMap((w) => this.dnsService.searchZones(w.domain)),
    shareReplay(1),
  );

  zone$ = this.zones$.pipe(
    tap((zones) => this.zoneEnabled.set(zones.length > 0)),
    map((zones) => zones[0]),
  );

  totalZoneRecords$ = this.zone$.pipe(map((zone) => zone?.records || []));

  zoneRecords$ = combineLatest([this.zone$, this.filters$]).pipe(
    map(([zone, filters]) => {
      const records = zone?.records ?? [];

      // Search
      const filteredRecords = filters.domainSearch
        ? records.filter((record) =>
            record.content.host?.toLowerCase().includes(filters.domainSearch!.toLowerCase()),
          )
        : records;

      // Pagination
      const start = (filters.page - 1) * filters.pageSize;
      const end = start + filters.pageSize;

      return filteredRecords.slice(start, end);
    }),
  );

  destroyed$ = new Subject();

  zoneEnabled = signal(false);

  loadingZone = signal(false);

  syncingZone = signal(false);

  recordsColumns = [
    {
      columnDef: 'host',
      header: 'Record Name / Host',
      cell: (element: DNSZoneRecord) => `${element.content.host}`,
    },
    {
      columnDef: 'type',
      header: 'Type',
      cell: (element: DNSZoneRecord) => `${element.type}`,
    },
    {
      columnDef: 'target',
      header: 'Target / Value',
      cell: (element: DNSZoneRecord) => `${element.target || element.content.target}`,
      useTemplate: true,
    },
    {
      columnDef: 'buttons',
      header: '',
      cell: () => '',
      useTemplate: true,
    },
  ];

  ngOnInit(): void {
    this.websocketService
      .listen<{ message: string }>('dialog')
      .pipe(
        takeUntil(this.destroyed$),
        filter(({ message }) => message === 'DNS Zone has been created.'),
      )
      .subscribe(() => {
        this.loadingZone.set(false);
        this.zoneEnabled.set(true);
        this.refresh$.next();
      });

    this.websocketService
      .listen<{ message: string }>('dialog')
      .pipe(
        takeUntil(this.destroyed$),
        filter(
          ({ message }) => message === `DNS Zone '${this.website().domain}' has been deleted.`,
        ),
      )
      .subscribe(() => {
        this.loadingZone.set(false);
        this.zoneEnabled.set(false);
        this.refresh$.next();
      });
  }

  onSearch(text: string) {
    this.filters.update((f) => ({ ...f, domainSearch: text }));
  }

  handlePageEvent(event: PageEvent) {
    this.filters.update((f) => ({ ...f, page: event.pageIndex + 1, pageSize: event.pageSize }));
  }

  createZone(): void {
    const serverId = this.websiteData().server?.id;
    if (serverId) {
      this.loadingZone.set(true);
      this.dnsService
        .createDNSZone({
          server: serverId,
          websiteId: this.website().id,
          adminOnly: false,
          overwrite: false,
        })
        .subscribe({
          next: () =>
            this.snackbar.openFromComponent(SnackbarComponent, {
              data: {
                text: `DNS zone creation started!`,
              },
            }),
          error: (error) => {
            this.statusModalService.open({
              state: 'error',
              message: error.error?.error,
            });
            this.loadingZone.set(false);
          },
        });
    }
  }

  deleteZone(): void {
    this.zone$
      .pipe(
        take(1),
        tap(() => this.loadingZone.set(true)),
        switchMap((zone) => this.dnsService.deleteDNSZone(zone.id)),
      )
      .subscribe({
        next: () =>
          this.snackbar.openFromComponent(SnackbarComponent, {
            data: {
              text: `DNS zone deletion started!`,
            },
          }),
        error: (error) => {
          this.statusModalService.open({
            state: 'error',
            message: error.error?.error,
          });
          this.loadingZone.set(false);
        },
      });
  }

  activateZone(): void {
    this.zone$
      .pipe(
        take(1),
        tap(() => this.loadingZone.set(true)),
        switchMap((zone) => this.dnsService.activateDNSZone(zone.id)),
      )
      .subscribe({
        next: () =>
          this.snackbar.openFromComponent(SnackbarComponent, {
            data: {
              text: `DNS zone activation started!`,
            },
          }),
        error: (error) => {
          this.statusModalService.open({
            state: 'error',
            message: error.error?.error,
          });
          this.loadingZone.set(false);
        },
      });
  }

  createDNSRecord(): void {
    this.zone$
      .pipe(
        take(1),
        switchMap((zone) =>
          this.dialog
            .open(AddDnsRecordDialogComponent, {
              data: { subTitle: this.website().domain, domain: zone.installation.domain },
            })
            .afterClosed()
            .pipe(
              filter(Boolean),
              switchMap((record) => this.dnsService.createDNSRecord(zone.id, record)),
            ),
        ),
      )
      .subscribe({
        next: () =>
          this.snackbar.openFromComponent(SnackbarComponent, {
            data: {
              text: `DNS Record creation started!`,
            },
          }),
        error: (error) => {
          this.statusModalService.open({
            state: 'error',
            message: error.error?.error,
          });
        },
      });
  }

  deleteDNSRecord(recordId: number): void {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: 'Delete Record',
          paragraphs: ['Are you sure you want to delete this DNS record?'],
          primaryButtonText: 'Delete Record',
          type: 'delete',
        },
      })
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap(() => this.zone$),
        take(1),
        switchMap((zone) => this.dnsService.deleteDNSRecord(zone.id, recordId)),
      )
      .subscribe({
        next: () =>
          this.snackbar.openFromComponent(SnackbarComponent, {
            data: {
              text: `DNS Record deleted!`,
            },
          }),
        error: (error) => {
          this.statusModalService.open({
            state: 'error',
            message: error.error?.error,
          });
        },
        complete: () => this.refresh$.next(),
      });
  }

  syncDNSZone(): void {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: 'Sync DNS Zone',
          paragraphs: [
            `This action will fetch the current DNS records of ${this.website().domain} from
              the DNS server and update the records displayed in the VMAX admin panel.`,
          ],

          primaryButtonText: 'Sync DNS Zone',
        },
      })
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap(() => this.zone$),
        take(1),
        switchMap((zone) =>
          this.dnsService.syncDNS({
            domain: zone.installation.domain,
            server: zone.installation.server,
            step: 0,
          }),
        ),
        tap(() => {
          this.snackbar.openFromComponent(SnackbarComponent, {
            data: {
              text: `DNS Zone sync started!`,
            },
          });
          this.syncingZone.set(true);
        }),
        switchMap(() =>
          this.websocketService.listen<{ message: string }>('dialog').pipe(
            filter(
              (message) => message.message === `DNS ${this.website().domain} Synced Successfully!`,
            ),
            take(1),
          ),
        ),
      )
      .subscribe({
        next: () =>
          this.statusModalService.open({
            state: 'success',
            title: 'DNS Zone Synced',
            message: `The DNS zone for ${this.website().domain} was synchronized successfully.`,
          }),
        error: (error) => {
          this.statusModalService.open({
            state: 'error',
            message: error.error?.error,
          });
        },
        complete: () => this.syncingZone.set(false),
      });
  }

  onCopied(success: boolean) {
    if (success) {
      this.snackbar.openFromComponent(SnackbarComponent, {
        data: {
          text: 'DNS Record copied!',
        },
      });
    }
  }

  toStatus(status: DNSZoneStatus): Status {
    switch (status) {
      case DNSZoneStatus.ACTIVE:
        return Status.ACTIVE;
      case DNSZoneStatus.DISABLED:
        return Status.DISABLED;
      case DNSZoneStatus.DUPLICATE:
        return Status.DUPLICATE;
      case DNSZoneStatus.PENDING:
        return Status.PENDING;
      default:
        return Status.ERROR;
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next('');
    this.destroyed$.complete();
  }
}
